/**
 * What a toolbar button does to the text — FRONTEND.md §9.169.
 *
 * **Pure, and separated from the textarea on purpose.** The interesting part is not the button; it
 * is what happens to a *selection*, and that has three cases a screen test would struggle to reach:
 * nothing selected, something selected, and something already wrapped. Each is a function of a
 * string and two offsets, so each is tested as one.
 *
 * **Toggling matters more than it sounds.** A bold button that only ever adds `**` turns a second
 * press into `****text****`, which renders as literal asterisks. Pressing it again must take the
 * marks off, which is what every editor does and what makes the toolbar usable rather than a trap.
 */

export interface MarkdownAction {
  key: string
  label: string
  title: string
  /** Wrapped around the selection — `**` for bold. Empty for a line action. */
  wrap?: string
  /** Put at the start of the line — `# ` for a heading. Empty for a wrap action. */
  prefix?: string
}

/**
 * The buttons, in the order they are drawn.
 *
 * `title` holds the Slovak source string rather than a translated one: this is module scope, and
 * `tt()` here would run before the translations load and cache the fallback forever. It is passed
 * through `tt()` where it is rendered, which is what makes the literal a key rather than a label.
 */
/* eslint-disable no-restricted-syntax -- translation keys, resolved with tt() at the call site. */
export const MARKDOWN_ACTIONS: readonly MarkdownAction[] = [
  { key: 'h1', label: 'H1', title: 'Nadpis 1', prefix: '# ' },
  { key: 'h2', label: 'H2', title: 'Nadpis 2', prefix: '## ' },
  { key: 'h3', label: 'H3', title: 'Nadpis 3', prefix: '### ' },
  { key: 'bold', label: 'B', title: 'Tučné', wrap: '**' },
  { key: 'italic', label: 'I', title: 'Šikmé', wrap: '*' },
  { key: 'strike', label: 'S', title: 'Preškrtnuté', wrap: '~~' },
  { key: 'code', label: '</>', title: 'Kód', wrap: '`' },
  { key: 'link', label: '🔗', title: 'Odkaz', wrap: '' },
  { key: 'ul', label: '•', title: 'Odrážky', prefix: '- ' },
  { key: 'ol', label: '1.', title: 'Číslovanie', prefix: '1. ' },
  { key: 'quote', label: '❝', title: 'Citát', prefix: '> ' },
  { key: 'hr', label: '―', title: 'Čiara', prefix: '' },
]
/* eslint-enable no-restricted-syntax */

export interface Edited {
  content: string
  /** Where the caret goes afterwards, so typing continues where the writer expects. */
  selectionStart: number
  selectionEnd: number
}

/** The start of the line the offset sits on. */
function lineStart(text: string, offset: number): number {
  const before = text.lastIndexOf('\n', Math.max(0, offset - 1))
  return before === -1 ? 0 : before + 1
}

export function applyAction(
  action: MarkdownAction,
  content: string,
  start: number,
  end: number,
): Edited {
  if (action.key === 'hr') {
    // Its own line, with a blank one after it: a rule glued to the next paragraph reads as a
    // setext heading in every markdown dialect, which would silently turn the line above into an H2.
    const from = lineStart(content, start)
    const inserted = '---\n\n'
    return {
      content: content.slice(0, from) + inserted + content.slice(from),
      selectionStart: from + inserted.length,
      selectionEnd: from + inserted.length,
    }
  }

  if (action.key === 'link') {
    const selected = content.slice(start, end)
    const label = selected === '' ? 'odkaz' : selected
    const inserted = `[${label}](https://)`
    return {
      content: content.slice(0, start) + inserted + content.slice(end),
      // Selecting the URL, which is the part that must be replaced. Selecting the whole thing would
      // mean the next keystroke deletes the label somebody had already written.
      selectionStart: start + inserted.length - 1,
      selectionEnd: start + inserted.length - 1,
    }
  }

  if (action.prefix !== undefined) {
    const from = lineStart(content, start)
    const line = content.slice(from)
    // Already prefixed with this exact marker: take it off. A heading button that only adds turns
    // `## x` into `# ## x`, which is not a heading of any level.
    if (line.startsWith(action.prefix)) {
      return {
        content: content.slice(0, from) + line.slice(action.prefix.length),
        selectionStart: Math.max(from, start - action.prefix.length),
        selectionEnd: Math.max(from, end - action.prefix.length),
      }
    }
    // A line carrying a *different* marker of the same family loses it first, so H1 → H2 is one
    // press rather than two and never `# ## `.
    const existing = /^(\s*)(#{1,6}\s|[-*+]\s|\d+[.)]\s|>\s?)/.exec(line)
    const stripped = existing === null ? line : line.slice(existing[0].length)
    const removed = existing === null ? 0 : existing[0].length
    return {
      content: content.slice(0, from) + action.prefix + stripped,
      selectionStart: start + action.prefix.length - removed,
      selectionEnd: end + action.prefix.length - removed,
    }
  }

  const wrap = action.wrap ?? ''
  const selected = content.slice(start, end)

  /**
   * §9.186 — a selection spanning lines is wrapped **per line**, not as a block.
   *
   * Markdown emphasis does not cross a newline, so bolding three list rows as one produced
   * `**- jedna\n- dva\n- tri**` and rendered as literal asterisks with the list broken in half.
   * Measured before this was written.
   *
   * Each line's **content** is wrapped and its marker left alone: `- **jedna**`, not
   * `- **- jedna**` — the second is a bullet whose text starts with a dash, not a bolder bullet.
   * A blank line is skipped, because wrapping nothing gives `****`.
   */
  if (wrap !== '' && selected.includes('\n')) {
    const wrapped = selected
      .split('\n')
      .map((line) => {
        const marker = /^(\s*(?:[-*+]\s+|\d+[.)]\s+|#{1,6}\s+|>\s?)?)(.*)$/.exec(line)
        const prefix = marker?.[1] ?? ''
        const body = marker?.[2] ?? line
        if (body.trim() === '') return line
        // Already wrapped line by line: take the marks off, so a second press undoes it.
        if (body.startsWith(wrap) && body.endsWith(wrap) && body.length > wrap.length * 2) {
          return `${prefix}${body.slice(wrap.length, body.length - wrap.length)}`
        }
        return `${prefix}${wrap}${body}${wrap}`
      })
      .join('\n')
    return {
      content: content.slice(0, start) + wrapped + content.slice(end),
      selectionStart: start,
      selectionEnd: start + wrapped.length,
    }
  }

  const before = content.slice(Math.max(0, start - wrap.length), start)
  const after = content.slice(end, end + wrap.length)

  // Already wrapped: unwrap. Without this a second press gives `****text****`, which renders as
  // literal asterisks rather than as bold — the trap this whole function exists to avoid.
  if (wrap !== '' && before === wrap && after === wrap) {
    return {
      content:
        content.slice(0, start - wrap.length) + selected + content.slice(end + wrap.length),
      selectionStart: start - wrap.length,
      selectionEnd: end - wrap.length,
    }
  }
  if (wrap !== '' && selected.startsWith(wrap) && selected.endsWith(wrap) && selected.length > wrap.length * 2) {
    const inner = selected.slice(wrap.length, selected.length - wrap.length)
    return {
      content: content.slice(0, start) + inner + content.slice(end),
      selectionStart: start,
      selectionEnd: start + inner.length,
    }
  }

  const inserted = `${wrap}${selected}${wrap}`
  return {
    content: content.slice(0, start) + inserted + content.slice(end),
    // Nothing selected: the caret lands between the marks, ready to type. Something selected: it
    // stays selected, so a second press undoes it.
    selectionStart: start + wrap.length,
    selectionEnd: start + wrap.length + selected.length,
  }
}
